// English source dictionary for MyPOS UI. Keys follow `<page>.<element>` or
// `common.*` / `nav.*` / `topbar.*`. The Lao mirror lives in lo.ts; missing
// or empty values there fall back to en automatically (see i18n/index.ts).
const en = {
    // ---------- Bulk import / Add many (page bodies) ----------
    "bulkImport.title": "Bulk Import",
    "bulkImport.subtitle": "Create products, units, categories, brands & stock from one Excel file.",
    "bulkImport.downloadTemplate": "Download template",
    "bulkImport.downloadCatalog": "Download current catalog",
    "bulkImport.working": "Working…",
    "bulkImport.preview": "Preview",
    "bulkImport.stockTip": "Tip: the Stock sheet is not re-runnable — after a successful Apply, clear it or use the set operation.",
    "bulkImport.previewSaved": "Preview (nothing saved yet)",
    "bulkImport.applied": "Applied",
    "bulkImport.errorsWord": "errors",
    "bulkImport.sumProducts": "Products",
    "bulkImport.newWord": "new",
    "bulkImport.updatedWord": "updated",
    "bulkImport.sumUnits": "Units",
    "bulkImport.sumBarcodes": "Barcodes generated",
    "bulkImport.barcodesWord": "barcodes",
    "bulkImport.sumCategories": "Categories",
    "bulkImport.sumBrands": "Brands",
    "bulkImport.sumSuppliers": "Suppliers",
    "bulkImport.sumStockMoves": "Stock moves",
    "bulkImport.colSheet": "Sheet",
    "bulkImport.colRow": "Row",
    "bulkImport.colProblem": "Problem",
    "addMany.title": "Add Multiple Products",
    "addMany.subtitle": "Type or paste rows straight from Excel, then Preview → Apply.",
    "addMany.addRows": "Add rows",
    "addMany.firstRow": "First: row",
    // ---------- Navigation (section titles) ----------
    "nav.till": "POS",
    "nav.overview": "Overview",
    "nav.salesMoney": "Sales & Money",
    "nav.catalog": "Catalog",
    "nav.inventory": "Inventory",
    "nav.admin": "Admin",
    "nav.shop": "Shop operations",
    // Manager renames the "Till" section to "POS" for owner-friendly browsing.
    "nav.posSection": "POS",
    // ---------- Navigation (per-item labels) ----------
    // Separate from `nav.<id>` permission keys (apps/api/src/permissions/permission-seed.ts).
    "navItem.pos": "POS",
    "navItem.held": "Held Bills",
    "navItem.recent": "Recent Sales",
    "navItem.quotes": "Quotations",
    "navItem.shift": "Shift",
    "navItem.tillSettings": "Settings",
    "navItem.dashboard": "Dashboard",
    "navItem.sales": "Sales History",
    "navItem.conflicts": "Sale Reviews",
    "navItem.shifts": "Shifts & Drawers",
    "navItem.expenses": "Expenses",
    "navItem.syncQueue": "Sync Queue",
    "navItem.products": "Products",
    "navItem.bulkImport": "Bulk Import",
    "navItem.addMany": "Add Multiple Products",
    "navItem.catbrand": "Categories & Brands",
    "navItem.promotions": "Promotions",
    "navItem.customers": "Customers",
    "navItem.suppliers": "Suppliers",
    "navItem.stock": "Stock by Store",
    "navItem.receive": "Receive Goods",
    "navItem.movements": "Stock Movements",
    "navItem.transfers": "Transfers",
    "navItem.count": "Stock Count",
    "navItem.staff": "Staff",
    "navItem.stores": "Stores",
    "navItem.reports": "Reports",
    "navItem.settings": "Global Settings",
    "navItem.audit": "Audit Log",
    // ---------- Topbar (Manager + Till) ----------
    "topbar.alertsOn": "Alerts on",
    "topbar.alertsBlocked": "Alerts blocked",
    "topbar.alertsUnavailable": "Alerts n/a",
    "topbar.enableAlerts": "Enable alerts",
    "topbar.install": "Install",
    // Till-only hardware/status pills.
    "topbar.store": "Store",
    "topbar.activeStore": "Active store",
    "topbar.selectStore": "— Select store —",
    "topbar.shiftBlocksStoreSwitch": "Close the active shift before switching stores.",
    "topbar.online": "Online",
    "topbar.offline": "Offline",
    "topbar.shiftOpen": "Shift open",
    "topbar.noShift": "No shift",
    "topbar.printerReady": "Printer ready",
    "topbar.printerNotSet": "Printer not set",
    "topbar.battery": "Battery",
    // ---------- App updates (confirm-update flow, Till desktop) ----------
    "update.checkNow": "Check for updates",
    "update.checking": "Checking…",
    "update.upToDate": "Up to date",
    "update.available": "Update available",
    "update.download": "Download update",
    "update.downloading": "Downloading",
    "update.downloaded": "Update downloaded",
    "update.restartToInstall": "Restart to install",
    "update.errorRetry": "Update failed — retry",
    "update.releaseNotes": "Release notes",
    "update.released": "Released",
    "update.noNotes": "No release notes for this version.",
    // ---------- Common ----------
    "common.walkIn": "Walk-in customer",
    "common.offline": "Offline",
    "common.online": "Online",
    "common.loading": "Loading…",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.confirm": "Confirm",
    "common.close": "Close",
    "common.search": "Search",
    "common.export": "Export",
    "common.import": "Import",
    "common.reprint": "Reprint",
    "common.void": "Void",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.view": "View",
    "common.filter": "Filter",
    "common.apply": "Apply",
    "common.reset": "Reset",
    "common.clear": "Clear",
    "common.all": "All",
    "common.none": "None",
    "common.yes": "Yes",
    "common.no": "No",
    "common.signOut": "Sign out",
    "common.signIn": "Sign in",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.submit": "Submit",
    "common.retry": "Retry",
    "common.refresh": "Refresh",
    "common.print": "Print",
    "common.download": "Download",
    "common.upload": "Upload",
    "common.copy": "Copy",
    "common.paste": "Paste",
    "common.notSet": "Not set",
    "common.never": "Never",
    "common.optional": "Optional",
    "common.required": "Required",
    "common.actions": "Actions",
    "common.status": "Status",
    "common.name": "Name",
    "common.amount": "Amount",
    "common.date": "Date",
    "common.time": "Time",
    "common.notes": "Notes",
    "common.note": "Note",
    "common.total": "Total",
    "common.subtotal": "Subtotal",
    "common.discount": "Discount",
    "common.tax": "Tax",
    "common.qty": "Qty",
    "common.price": "Price",
    "common.cost": "Cost",
    "common.sku": "SKU",
    "common.barcode": "Barcode",
    "common.category": "Category",
    "common.brand": "Brand",
    "common.supplier": "Supplier",
    "common.store": "Store",
    "common.cashier": "Cashier",
    "common.customer": "Customer",
    "common.product": "Product",
    "common.email": "Email",
    "common.phone": "Phone",
    "common.password": "Password",
    "common.role": "Role",
    "common.active": "Active",
    "common.inactive": "Inactive",
    "common.enabled": "Enabled",
    "common.disabled": "Disabled",
    "common.empty": "Empty",
    "common.noResults": "No results",
    "common.allStores": "All stores",
    "common.allCashiers": "All cashiers",
    "common.allCategories": "All categories",
    "common.allBrands": "All brands",
    "common.allSuppliers": "All suppliers",
    "common.allStatuses": "All statuses",
    "common.allCustomers": "All customers",
    "common.errorOccurred": "An error occurred",
    "common.tryAgain": "Try again",
    // ---------- POS / Pos.tsx ----------
    "pos.scanPlaceholder": "Scan barcode or search by name…",
    "pos.cartEmpty": "Cart is empty. Scan or tap a product to start.",
    "pos.noProductsMatch": "No products match.",
    "pos.barcodeMatch": "Barcode match — tap to add this pack size",
    "pos.payCash": "Cash",
    "pos.payQr": "QR / Transfer",
    "pos.split": "Split",
    "pos.credit": "Credit",
    "pos.hold": "Hold",
    "pos.recall": "Recall",
    "pos.discount": "Discount",
    "pos.redeem": "Redeem",
    "pos.payLabel": "PAY",
    "pos.clearCart": "Clear cart",
    "pos.attachCustomer": "Attach",
    "pos.changeCustomer": "Change",
    "pos.itemsLine": "items",
    "pos.lineSingular": "line",
    "pos.linePlural": "lines",
    "pos.points": "pts",
    "pos.tab": "tab",
    "pos.allCategories": "All",
    "pos.payTotalLabel": "PAY",
    // ---------- POS modals ----------
    "customerLookup.title": "Find or attach customer",
    "customerLookup.searchPlaceholder": "Name or phone…",
    "customerLookup.attach": "Attach",
    "customerLookup.detach": "Walk-in (no customer)",
    "customerLookup.create": "Create new customer",
    "customerLookup.namePlaceholder": "Customer name",
    "customerLookup.phonePlaceholder": "Phone (optional)",
    "customerLookup.emailPlaceholder": "Email (optional)",
    "customerLookup.notesPlaceholder": "Notes (optional)",
    "heldOrders.title": "Held orders",
    "heldOrders.empty": "No orders are currently on hold.",
    "heldOrders.resume": "Resume",
    "heldOrders.discard": "Discard",
    "loyalty.title": "Redeem loyalty points",
    "loyalty.available": "Available",
    "loyalty.redeemAmount": "Points to redeem",
    "loyalty.discountValue": "Discount value",
    "loyalty.confirm": "Apply redemption",
    "credit.title": "Pay on credit",
    "credit.outstanding": "Outstanding",
    "credit.creditLimit": "Credit limit",
    "credit.afterSale": "After this sale",
    "credit.offlineBlocked": "Credit sales require an online connection.",
    "credit.confirm": "Charge to account",
    "split.title": "Split payment",
    "split.addLine": "Add tender",
    "split.removeLine": "Remove",
    "split.method": "Method",
    "split.amount": "Amount",
    "split.balance": "Balance",
    "split.confirm": "Confirm split",
    // ---------- POS · Cash confirmation modal ----------
    "cashPay.title": "Confirm cash payment",
    "cashPay.saleTotal": "Sale total",
    "cashPay.tendered": "Cash tendered",
    "cashPay.exact": "Exact",
    "cashPay.short": "Short",
    "cashPay.changeDue": "Change due",
    "cashPay.hint": "Tendered amount must be at least the sale total. Press Enter to confirm.",
    "cashPay.confirm": "Confirm cash sale",
    "noSale.openDrawer": "Open drawer",
    "noSale.title": "Open cash drawer",
    "noSale.intro": "Opening the drawer without a sale. Choose a reason — it is recorded for the manager.",
    "noSale.reasonLabel": "Reason",
    "noSale.noteLabel": "Note (optional)",
    "noSale.notePlaceholder": "Add detail if helpful",
    "noSale.confirm": "Open drawer",
    "noSale.reasonMakeChange": "Make change",
    "noSale.reasonRemoveCash": "Remove cash",
    "noSale.reasonWrongAmount": "Wrong amount",
    "noSale.reasonCustomerRefund": "Customer refund",
    "noSale.reasonOther": "Other",
    // ---------- POS · QR / Bank-transfer confirmation modal ----------
    "qrPay.title": "Confirm QR / Transfer payment",
    "qrPay.saleTotal": "Sale total",
    "qrPay.step1": "Customer pays via QR or bank transfer to the shop account.",
    "qrPay.step2": "Customer shows the bank slip on their phone.",
    "qrPay.step3": "Scan the OneProof QR on the slip — the transaction ID will fill in below.",
    "qrPay.reference": "Transaction ID",
    "qrPay.referencePlaceholder": "Scan the OneProof QR on the bank slip",
    "qrPay.referenceHint": "Tip: focus this field, then scan. The barcode scanner will fill it for you. You can also paste or type manually.",
    "qrPay.acknowledge": "I verified the payment was received.",
    "qrPay.confirm": "Confirm QR / Transfer sale",
    // ---------- Held bills (page) ----------
    "heldBills.title": "Held Bills",
    "heldBills.empty": "No bills are currently on hold.",
    "heldBills.resume": "Resume",
    "heldBills.discard": "Discard",
    "heldBills.openedAt": "Opened",
    "heldBills.itemCount": "items",
    // Page subtitle + empty-state hint shown when the held-bills queue is empty.
    "heldBills.subtitleEmpty": "No parked sales",
    "heldBills.subtitleCount": "parked sales",
    "heldBills.cartEmptyHint": "No held bills. Tap Hold on the POS to park a sale here.",
    // Per-card title. {name} is substituted with the cart's customer; walk-in is
    // used when the held cart had no customer attached.
    "heldBills.walkInTitle": "Walk-in customer's held bill",
    "heldBills.namedTitle": "{name}'s held bill",
    // ---------- Login ----------
    "login.title": "Sign in",
    "login.email": "Username",
    "login.username": "Username",
    "login.password": "Password",
    "login.signIn": "Sign in",
    "login.invalid": "Invalid username or password.",
    // ---------- Forced password change ----------
    "changePassword.title": "Change your password",
    "changePassword.subtitle": "You must change your password before continuing.",
    "changePassword.currentPassword": "Current password",
    "changePassword.newPassword": "New password",
    "changePassword.confirmPassword": "Confirm new password",
    "changePassword.errorMismatch": "Passwords do not match",
    "changePassword.errorMinLength": "Password must be at least 8 characters",
    "changePassword.submit": "Change password",
    // ---------- Recent sales ----------
    "recentSales.title": "Recent Sales",
    "recentSales.empty": "No sales today yet.",
    "recentSales.reprint": "Reprint",
    "recentSales.void": "Void",
    "recentSales.colTime": "Time",
    "recentSales.colItems": "Items",
    "recentSales.colTender": "Tender",
    "recentSales.colTotal": "Total",
    "recentSales.colCustomer": "Customer",
    "recentSales.colReceipt": "Receipt",
    "recentSales.colCashier": "Cashier",
    "recentSales.rangeToday": "Today",
    "recentSales.range7d": "Last 7 days",
    "recentSales.range30d": "Last 30 days",
    "recentSales.subtitle": "sales in range",
    "recentSales.subtitleOne": "sale in range",
    "recentSales.noneInRange": "No sales in this range.",
    "recentSales.detailHeading": "Sale detail",
    "recentSales.walkInShort": "Walk-in",
    "recentSales.statusCompleted": "Completed",
    "recentSales.statusVoided": "Voided",
    "recentSales.statusRefunded": "Refunded",
    "recentSales.statusConflictPending": "Conflict pending",
    "recentSales.voidBlockedVoided": "This sale is already voided.",
    "recentSales.voidBlockedRefunded": "Refunded sales cannot be voided.",
    "recentSales.voidBlockedConflict": "Resolve the open conflict for this sale before voiding it.",
    // ---------- Receipt ----------
    "receipt.saleComplete": "Sale complete",
    "receipt.printNotSent": "Receipt was not sent to the printer",
    // ---------- Quotations ----------
    "quotations.title": "Quotations",
    "quotations.empty": "No quotations yet.",
    "quotations.create": "New quotation",
    "quotations.colNumber": "Number",
    "quotations.colCustomer": "Customer",
    "quotations.colTotal": "Total",
    "quotations.colStatus": "Status",
    "quotations.colCreatedAt": "Created",
    "quotations.print": "Print",
    "quotations.duplicate": "Duplicate",
    "quotations.markPaid": "Mark paid",
    "quotations.markUnpaid": "Mark unpaid",
    "quotations.filterUnpaid": "Unpaid",
    "quotations.filterPaid": "Paid",
    "quotations.filterAll": "All",
    "quotations.delete": "Delete",
    "quotations.edit": "Edit",
    "quotations.save": "Save",
    "quotations.cancel": "Cancel",
    "quotations.addLine": "Add line",
    "quotations.removeLine": "Remove",
    "quotations.modalTitle": "New quotation",
    "quotations.customerLabel": "Customer",
    "quotations.validUntil": "Valid until",
    "quotations.noteLabel": "Note (optional)",
    "quotations.lineUnit": "Unit price",
    "quotations.lineQty": "Qty",
    "quotations.lineTotal": "Total",
    "quotations.lastPrinted": "Last printed",
    "quotations.searchProduct": "Search products…",
    "quotations.searchCustomer": "Search customers…",
    "quotations.walkIn": "Walk-in customer",
    // Status badge values rendered next to each quotation row.
    "quotations.statusPaid": "paid",
    "quotations.statusUnpaid": "unpaid",
    // Composed labels (number + suffix). Rendered as `${n} ${suffix}`.
    "quotations.subtitleUnpaid": "unpaid quotations",
    "quotations.subtitlePaid": "paid quotations",
    "quotations.subtitleAll": "all quotations",
    "quotations.validUntilShort": "valid until",
    "quotations.lineCount": "lines",
    // Inline labels in the editor pane and the empty-pane prompt.
    "quotations.selectPrompt": "Select a quotation on the left to preview + act on it.",
    "quotations.colProduct": "Product",
    "quotations.linked": "linked",
    "quotations.notePrefix": "Note",
    "quotations.closeEditor": "Close editor",
    // ---------- Shift ----------
    "shift.title": "Shift & Cash drawer",
    "shift.openShift": "Open drawer",
    "shift.closeShift": "Close drawer",
    "shift.openingFloat": "Opening float",
    "shift.countedAmount": "Counted amount",
    "shift.payIn": "Pay-in",
    "shift.payOut": "Pay-out",
    "shift.note": "Note",
    "shift.expectedCash": "Expected cash",
    "shift.diff": "Difference",
    "shift.movements": "Movements",
    "shift.startedAt": "Started",
    // Header + KPI labels on the Shift close screen.
    "shift.openedAtPrefix": "Opened",
    "shift.cashierPrefix": "cashier",
    "shift.kpiCashSales": "Cash sales",
    "shift.kpiExpenses": "Expenses",
    "shift.kpiExpected": "Expected",
    "shift.tenderSplit": "Tender split",
    "shift.cashTimeline": "Cash timeline",
    "shift.denominationCount": "Denomination count",
    "shift.countedCashDirect": "Counted cash (direct entry)",
    "shift.differenceLabel": "Difference",
    "shift.varianceWithin1k": "Within ±1,000 ₭ — good to close.",
    "shift.varianceWithin50k": "Within ±50,000 ₭ — acceptable, needs sign-off.",
    "shift.varianceOver50k": "Above ±50,000 ₭ threshold — manager approval required.",
    "shift.callManager": "Call manager over",
    "shift.closeAndPrint": "Close shift & print summary",
    "shift.expectedSep": "expected",
    "shift.countedSep": "counted",
    "shift.totalCounted": "Total counted",
    "shift.colMethod": "Method",
    "shift.colCount": "Count",
    "shift.colTotal": "Total",
    "shift.noSalesYet": "No sales yet.",
    "shift.entrySale": "Sale",
    "shift.entryExpense": "Expense",
    "shift.entryMovementIn": "Movement in",
    "shift.entryMovementOut": "Movement out",
    // ---------- Till settings ----------
    "tillSettings.title": "Settings",
    "tillSettings.version": "Version",
    "tillSettings.pwaHardwareNote": "You're on the web app. The receipt printer, cash drawer, and label printer all live on the desktop Till — these fields are shown for reference only. Open the desktop app to change hardware.",
    "tillSettings.printer": "Receipt printer",
    "tillSettings.drawer": "Cash drawer",
    "tillSettings.connection": "Connection",
    "tillSettings.language": "Language",
    "tillSettings.catalogSync": "Catalog sync",
    "tillSettings.receiptLayout": "Receipt layout",
    "tillSettings.dangerZone": "Danger zone",
    "tillSettings.testPrint": "Test print",
    "tillSettings.testKick": "Test kick",
    "tillSettings.resyncNow": "Resync now",
    "tillSettings.lastSync": "Last successful sync",
    "tillSettings.testOk": "Last test: OK",
    "tillSettings.testError": "Last test: failed",
    "tillSettings.testing": "Testing…",
    "tillSettings.notTested": "Not tested",
    "tillSettings.transportWindows": "Windows printer (USB / installed)",
    "tillSettings.transportNetwork": "Network (TCP 9100)",
    "tillSettings.transportFile": "File (share / pipe)",
    "tillSettings.transportNone": "None (disabled)",
    "tillSettings.windowsPrinter": "Windows printer",
    "tillSettings.choosePrinter": "— Choose a printer —",
    "tillSettings.printerDefaultTag": "(default)",
    "tillSettings.noPrintersFound": "No installed printers found. Install the printer in Windows first, then reopen Settings.",
    "tillSettings.labelPrinter": "Label printer",
    "tillSettings.testLabel": "Test label",
    "tillSettings.labelPrinterHint": "Prints product price/barcode tags. Use a Windows printer or a network address, same as the receipt printer.",
    "tillSettings.enabledOption": "Enabled",
    "tillSettings.disabledOption": "Disabled",
    "tillSettings.host": "Host / IP",
    "tillSettings.port": "Port",
    "tillSettings.paperWidth": "Paper width (mm)",
    "tillSettings.codepage": "Codepage (ESC t)",
    "tillSettings.drawerPin": "Drawer pin",
    "tillSettings.drawerPin0": "Pin 0 (most printers)",
    "tillSettings.drawerPin1": "Pin 1",
    "tillSettings.kickOnCash": "Auto-kick on cash sale",
    "tillSettings.kickOnCashYes": "Kick on cash tender",
    "tillSettings.kickOnCashNo": "Manual only",
    "tillSettings.apiBaseUrl": "API base URL",
    "tillSettings.syncInterval": "Sync interval (seconds)",
    "tillSettings.interfaceLanguage": "Interface language",
    "tillSettings.languageHint": "Receipt layout is not affected — it's set per printer.",
    "tillSettings.never": "Never",
    "tillSettings.cacheHint": "Till caches the catalog locally. Offline sales queue until sync reaches the server.",
    "tillSettings.receiptHeader": "Header",
    "tillSettings.receiptFooter": "Footer",
    "tillSettings.dangerHint": "Clearing the cache removes cached products, held bills, and cashed reports. Queued (un-synced) sales are preserved.",
    "tillSettings.clearCache": "Clear local cache",
    "tillSettings.signOut": "Sign out",
    "tillSettings.languageEnglish": "English",
    "tillSettings.languageLao": "ລາວ (Lao)",
    // ---------- Manage / Dashboard ----------
    "dashboard.title": "Dashboard",
    "dashboard.kpiSalesToday": "Sales today",
    "dashboard.kpiOrdersToday": "Orders today",
    "dashboard.kpiAvgOrder": "Avg order",
    "dashboard.kpiOpenDrawers": "Open drawers",
    "dashboard.recentSales": "Recent sales",
    "dashboard.lowStock": "Low stock",
    "dashboard.alerts": "Alerts",
    "dashboard.topProducts": "Top products",
    // ---------- Sales History ----------
    "salesHistory.title": "Sales History",
    "salesHistory.filterStore": "Store",
    "salesHistory.filterCashier": "Cashier",
    "salesHistory.filterTender": "Tender",
    "salesHistory.filterStatus": "Status",
    "salesHistory.filterDateFrom": "From",
    "salesHistory.filterDateTo": "To",
    "salesHistory.filterSearch": "Search receipt # / customer / reference",
    "salesHistory.colTime": "Time",
    "salesHistory.colReceipt": "Receipt",
    "salesHistory.colCashier": "Cashier",
    "salesHistory.colCustomer": "Customer",
    "salesHistory.colItems": "Items",
    "salesHistory.colTender": "Tender",
    "salesHistory.colTotal": "Total",
    "salesHistory.colStatus": "Status",
    "salesHistory.empty": "No sales match these filters.",
    "salesHistory.export": "Export",
    // ---------- Customers ----------
    "customers.title": "Customers",
    "customers.create": "Add customer",
    "customers.edit": "Edit customer",
    "customers.searchPlaceholder": "Search name or phone…",
    "customers.colName": "Name",
    "customers.colPhone": "Phone",
    "customers.colTier": "Tier",
    "customers.colPoints": "Points",
    "customers.colBalance": "Balance",
    "customers.colStore": "Home store",
    "customers.empty": "No customers match.",
    // ---------- Products ----------
    "products.title": "Products",
    "products.create": "Add product",
    "products.import": "Import",
    "products.export": "Export",
    "products.bulkPrice": "Bulk price adjust",
    "products.searchPlaceholder": "Search name, ID, barcode…",
    "products.filterCategory": "Category",
    "products.filterBrand": "Brand",
    "products.filterSupplier": "Supplier",
    "products.filterLowStock": "Low stock only",
    "products.filterStatus": "Status",
    "products.colName": "Name",
    "products.colSku": "ID",
    "products.colBarcode": "Barcode",
    "products.colCategory": "Category",
    "products.colBrand": "Brand",
    "products.colPrice": "Price",
    "products.colCost": "Cost",
    "products.colStock": "Stock",
    "products.colStatus": "Status",
    "products.empty": "No products match.",
    // ---------- Add product modal ----------
    "addProduct.title": "Add product",
    "addProduct.editTitle": "Edit product",
    "addProduct.basics": "Basics",
    "addProduct.pricing": "Pricing",
    "addProduct.units": "Units",
    "addProduct.stock": "Stock per store",
    "addProduct.tags": "Tags",
    "addProduct.save": "Save product",
    "addProduct.cancel": "Cancel",
    "addProduct.namePlaceholder": "Product name",
    "addProduct.skuPlaceholder": "Auto-generated, e.g. 10010",
    "addProduct.barcodePlaceholder": "Barcode",
    "addProduct.image": "Photo",
    "addProduct.imageChoose": "Choose photo",
    "addProduct.imageReplace": "Replace photo",
    "addProduct.imageRemove": "Remove",
    "addProduct.imageProcessing": "Processing…",
    "addProduct.imageHint": "JPG/PNG. Auto-resized to fit the tile.",
    "addProduct.imageReadError": "Couldn't read that image. Try a JPG or PNG.",
    // Tabs
    "addProduct.tabGeneral": "General",
    "addProduct.tabUnits": "Units & Pricing",
    "addProduct.tabInventory": "Inventory",
    "addProduct.tabLabels": "Labels",
    // General tab
    "addProduct.name": "Name",
    "addProduct.nameExample": "e.g. Coca-Cola 500ml",
    "addProduct.id": "ID",
    "addProduct.barcode": "Barcode",
    "addProduct.category": "Category",
    "addProduct.brand": "Brand",
    "addProduct.supplier": "Supplier",
    "addProduct.select": "Select…",
    "addProduct.saveChanges": "Save changes",
    "addProduct.saving": "Saving…",
    // Units & Pricing tab
    "addProduct.costPerBase": "Cost (per base unit)",
    "addProduct.sellPricePerBase": "Sell price (per base unit)",
    "addProduct.margin": "Margin:",
    "addProduct.sellableUnits": "Sellable units",
    "addProduct.sellableUnitsHint": "Pick the base unit (kg, piece, etc.) and add bigger packs (10kg bag, 50kg sack). Ratio is \"how many base units in this pack\".",
    "addProduct.addUnit": "Add unit",
    "addProduct.unitName": "Unit name",
    "addProduct.unitNameBasePlaceholder": "kg / piece / bag",
    "addProduct.unitNamePackPlaceholder": "10kg bag",
    "addProduct.ratioToBase": "Ratio to base",
    "addProduct.sellPrice": "Sell price",
    "addProduct.usesBase": "(uses base)",
    "addProduct.setBase": "Set base",
    "addProduct.base": "BASE",
    "addProduct.makeBase": "Make this the base unit",
    "addProduct.removeUnit": "Remove unit",
    "addProduct.unitsExample": "Example: feed sold by kg → base \"kg\", add \"10kg bag\" with ratio 10 and price 95,000 ₭, \"50kg sack\" with ratio 50 and price 450,000 ₭.",
    // Inventory tab
    "addProduct.reorderPoint": "Reorder point",
    "addProduct.openingStock": "Opening stock",
    "addProduct.stockEditDisabled": "Editing stock here is disabled — use Receive Goods or Stock Count to change quantities so the audit log captures the reason.",
    // Labels tab
    "addProduct.labelProductName": "Product name",
    "addProduct.labelBarcode": "barcode",
    "addProduct.labelPreview": "40 × 30 mm preview",
    "addProduct.print1Label": "Print 1 label",
    "addProduct.printLabelTillOnly": "Label printing is Till-only; open this on a register with a label printer attached.",
    "addProduct.printLabelAria": "Save product and print 1 label",
    // ---------- Add expense modal ----------
    "addExpense.title": "Add expense",
    "addExpense.save": "Save expense",
    "addExpense.saving": "Saving…",
    "addExpense.cancel": "Cancel",
    "addExpense.category": "Category",
    "addExpense.date": "Date",
    "addExpense.amount": "Amount (₭)",
    "addExpense.amountPlaceholder": "0",
    "addExpense.payMethod": "Pay method",
    "addExpense.description": "Description (optional)",
    "addExpense.descriptionPlaceholder": "e.g. Office supplies — Tuesday market",
    "addExpense.newCategoryPlaceholder": "e.g. Utilities",
    "addExpense.saveCategory": "Save",
    "addExpense.new": "New",
    "addExpense.payCash": "Cash (from drawer)",
    "addExpense.payBank": "Bank transfer",
    "addExpense.payOther": "Other",
    "addExpense.noCategoriesAdmin": "No expense categories are configured. Ask an admin to create at least one category before recording expenses.",
    "addExpense.noCategoriesHint": "No categories yet — type a name above and click Save to create your first one (e.g. Rent, Utilities, Wages).",
    "addExpense.deleteConfirm": "Delete \"{name}\" from future expenses? Existing expense rows will keep their history.",
    // aria-labels
    "addExpense.newCategoryNameAria": "New category name",
    "addExpense.saveNewCategoryAria": "Save new category",
    "addExpense.cancelNewCategoryAria": "Cancel new category",
    "addExpense.expenseCategoryAria": "Expense category",
    "addExpense.addNewCategoryAria": "Add new category",
    "addExpense.deleteCategoryAria": "Delete category {name}",
    "addExpense.payDateAria": "Pay date",
    "addExpense.amountAria": "Expense amount",
    "addExpense.descriptionAria": "Expense description",
    // ---------- Bulk price adjust ----------
    "bulkPrice.title": "Bulk price adjust",
    "bulkPrice.percent": "Percent",
    "bulkPrice.up": "Increase",
    "bulkPrice.down": "Decrease",
    "bulkPrice.roundTo": "Round to",
    "bulkPrice.affected": "Affected products",
    "bulkPrice.apply": "Apply",
    // ---------- Import products modal ----------
    "importProducts.title": "Import products",
    "importProducts.uploadCta": "Choose file",
    "importProducts.dropHint": "Drop CSV or XLSX here",
    "importProducts.preview": "Preview",
    "importProducts.column": "Column",
    "importProducts.mapTo": "Maps to",
    "importProducts.rows": "Rows",
    "importProducts.errors": "Errors",
    "importProducts.commit": "Import",
    "importProducts.cancel": "Cancel",
    "importProducts.success": "Import complete.",
    "importProducts.failed": "Import failed.",
    // ---------- Categories & Brands ----------
    "categoriesBrands.title": "Categories & Brands",
    "categoriesBrands.categories": "Categories",
    "categoriesBrands.brands": "Brands",
    "categoriesBrands.addCategory": "Add category",
    "categoriesBrands.addBrand": "Add brand",
    "categoriesBrands.empty": "No items yet.",
    // ---------- Sale Reviews (internal key: "conflicts") ----------
    "conflicts.title": "Sale Reviews",
    "conflicts.empty": "No sale reviews pending.",
    "conflicts.colKind": "Kind",
    "conflicts.colEntity": "Entity",
    "conflicts.colLocal": "Local",
    "conflicts.colServer": "Server",
    "conflicts.colResolvedBy": "Resolved by",
    "conflicts.acceptLocal": "Keep local",
    "conflicts.acceptServer": "Use server",
    // ---------- Expenses ----------
    "expenses.title": "Expenses",
    "expenses.create": "Add expense",
    "expenses.colDate": "Date",
    "expenses.colCategory": "Category",
    "expenses.colAmount": "Amount",
    "expenses.colPaidFrom": "Paid from",
    "expenses.colDescription": "Description",
    "expenses.empty": "No expenses recorded.",
    "expenses.searchPlaceholder": "Search ID, date, category, description, amount, or recorder…",
    // ---------- Receive Goods ----------
    "receiveGoods.title": "Receive Goods",
    "receiveGoods.create": "New receiving",
    "receiveGoods.supplier": "Supplier",
    "receiveGoods.invoice": "Invoice #",
    "receiveGoods.received": "Received at",
    "receiveGoods.colSku": "SKU",
    "receiveGoods.colName": "Name",
    "receiveGoods.colQty": "Qty",
    "receiveGoods.colCost": "Cost",
    "receiveGoods.empty": "No receivings yet.",
    "receiveGoods.manualRow": "Manual row",
    "receiveGoods.vat": "VAT",
    // ---------- Product Lookup (shared picker) ----------
    "productLookup.title": "Find product",
    "productLookup.searchPlaceholder": "Search by name, SKU, or barcode…",
    "productLookup.noMatches": "No products match.",
    "productLookup.startTyping": "Start typing to search products.",
    "productLookup.searching": "Searching…",
    // ---------- Stock by Store ----------
    "stockByStore.title": "Stock by Store",
    "stockByStore.colSku": "SKU",
    "stockByStore.colProduct": "Product",
    "stockByStore.colTotal": "Total",
    "stockByStore.empty": "No stock data.",
    // ---------- Stock Count ----------
    "stockCount.title": "Stock Count",
    "stockCount.create": "New count",
    "stockCount.colSku": "SKU",
    "stockCount.colProduct": "Product",
    "stockCount.colExpected": "Expected",
    "stockCount.colCounted": "Counted",
    "stockCount.colVariance": "Difference",
    "stockCount.empty": "No counts yet.",
    "stockCount.commit": "Commit count",
    "stockCount.confirmTitle": "Start new stock count?",
    "stockCount.confirmStart": "Start count",
    // ---------- Stock Movements ----------
    "stockMovements.title": "Stock Movements",
    "stockMovements.colDate": "Date",
    "stockMovements.colProduct": "Product",
    "stockMovements.colKind": "Kind",
    "stockMovements.colQty": "Qty",
    "stockMovements.colNote": "Note",
    "stockMovements.empty": "No movements.",
    // ---------- Suppliers ----------
    "suppliers.title": "Suppliers",
    "suppliers.create": "Add supplier",
    "suppliers.searchPlaceholder": "Search supplier…",
    "suppliers.colName": "Name",
    "suppliers.colPhone": "Phone",
    "suppliers.colEmail": "Email",
    "suppliers.colNote": "Note",
    "suppliers.empty": "No suppliers.",
    // ---------- Transfers ----------
    "transfers.title": "Transfers",
    "transfers.create": "New transfer",
    "transfers.fromStore": "From",
    "transfers.toStore": "To",
    "transfers.colCreated": "Created",
    "transfers.colFrom": "From",
    "transfers.colTo": "To",
    "transfers.colItems": "Items",
    "transfers.colStatus": "Status",
    "transfers.empty": "No transfers.",
    // ---------- Promotions ----------
    "promotions.title": "Promotions",
    "promotions.create": "New promotion",
    "promotions.colName": "Name",
    "promotions.colKind": "Kind",
    "promotions.colStart": "Starts",
    "promotions.colEnd": "Ends",
    "promotions.colStatus": "Status",
    "promotions.empty": "No promotions yet.",
    "promotions.builderTitle": "Promotion builder",
    "promotions.builderRule": "Rule",
    "promotions.builderReward": "Reward",
    "promotions.builderSchedule": "Schedule",
    "promotions.builderConditions": "Conditions",
    // ---------- Shifts & Drawers (manager view) ----------
    "shiftsDrawers.title": "Shifts & Drawers",
    "shiftsDrawers.colStore": "Store",
    "shiftsDrawers.colCashier": "Cashier",
    "shiftsDrawers.colOpenedAt": "Opened",
    "shiftsDrawers.colClosedAt": "Closed",
    "shiftsDrawers.colExpected": "Expected",
    "shiftsDrawers.colCounted": "Counted",
    "shiftsDrawers.colVariance": "Difference",
    "shiftsDrawers.empty": "No drawer sessions.",
    // ---------- Admin / Staff ----------
    "staff.title": "Staff",
    "staff.create": "Add staff member",
    "staff.searchPlaceholder": "Search name or email…",
    "staff.filterRole": "Role",
    "staff.filterStore": "Store",
    "staff.filterStatus": "Status",
    "staff.colName": "Name",
    "staff.colEmail": "Email",
    "staff.colRole": "Role",
    "staff.colStore": "Store",
    "staff.colLastActive": "Last active",
    "staff.colStatus": "Status",
    "staff.empty": "No staff match.",
    "staff.tabProfile": "Profile",
    "staff.tabRoleStore": "Role & store",
    "staff.tabPermissions": "Permissions",
    "staff.tabPassword": "Password",
    "staff.tabActivity": "Activity",
    // ---------- Stores ----------
    "stores.title": "Stores",
    "stores.create": "Add store",
    "stores.colName": "Name",
    "stores.colCode": "Code",
    "stores.colAddress": "Address",
    "stores.colPhone": "Phone",
    "stores.colStatus": "Status",
    "stores.empty": "No stores yet.",
    // ---------- Reports ----------
    "reports.title": "Reports",
    "reports.tabSales": "Sales",
    "reports.tabProducts": "Products",
    "reports.tabCashiers": "Cashiers",
    "reports.tabCustomers": "Customers",
    "reports.tabInventory": "Inventory",
    "reports.tabExpenses": "Expenses",
    "reports.tabPromotions": "Promotions",
    "reports.exportXlsx": "Export XLSX",
    "reports.exportAllXlsx": "Export all XLSX",
    "reports.dateRange": "Date range",
    "reports.empty": "No data for this range.",
    // ---------- Audit Log ----------
    "auditLog.title": "Audit Log",
    "auditLog.filterActor": "Actor",
    "auditLog.filterEntity": "Entity",
    "auditLog.filterAction": "Action",
    "auditLog.filterDateFrom": "From",
    "auditLog.filterDateTo": "To",
    "auditLog.colTime": "Time",
    "auditLog.colActor": "Actor",
    "auditLog.colAction": "Action",
    "auditLog.colEntity": "Entity",
    "auditLog.colDetails": "Details",
    "auditLog.empty": "No log entries match.",
    "auditLog.export": "Export",
    // Header subtitle: rendered as `${n} events · immutable · exportable`.
    "auditLog.subtitleEvents": "events",
    "auditLog.subtitleImmutable": "immutable",
    "auditLog.subtitleExportable": "exportable",
    "auditLog.allActors": "All actors",
    "auditLog.allActions": "All actions",
    "auditLog.allTables": "All tables",
    "auditLog.dateRangeSep": "to",
    "auditLog.onPrefix": "on",
    "auditLog.diffField": "Field",
    "auditLog.diffBefore": "Before",
    "auditLog.diffAfter": "After",
    "auditLog.noFieldChanges": "No field-level changes.",
    "auditLog.loadMore": "Load more",
    "auditLog.collapse": "Collapse",
    "auditLog.expand": "Expand",
    // Friendly labels for audit-action enums (shown in the Action dropdown
    // and on each entry's pill). Falls back to the raw enum value if missing.
    "auditAction.user_login": "User login",
    "auditAction.user_login_password": "Login with password",
    "auditAction.user_create": "User created",
    "auditAction.user_updated": "User updated",
    "auditAction.user_deactivate": "User deactivated",
    "auditAction.permission_change": "Permission changed",
    "auditAction.price_change": "Price changed",
    "auditAction.void_sale": "Sale voided",
    "auditAction.apply_discount": "Discount applied",
    "auditAction.stock_adjust": "Stock adjusted",
    "auditAction.receive_goods": "Goods received",
    "auditAction.transfer_send": "Transfer sent",
    "auditAction.transfer_receive": "Transfer received",
    "auditAction.stocktake_submit": "Count submitted",
    "auditAction.stocktake_approve": "Count approved",
    "auditAction.conflict_resolve": "Sale review resolved",
    "auditAction.drawer_opened": "Drawer opened",
    "auditAction.drawer_closed": "Drawer closed",
    "auditAction.promotion_create": "Promotion created",
    "auditAction.promotion_pause": "Promotion paused",
    "auditAction.product_create": "Product created",
    "auditAction.customer_created": "Customer created",
    "auditAction.customer_credit": "Credit recorded",
    "auditAction.supplier_payment": "Supplier payment",
    "auditAction.expense_create": "Expense recorded",
    "auditAction.hold_order": "Held order",
    "auditAction.held_order_created": "Held order created",
    "auditAction.settings_change": "Settings changed",
    "auditAction.store_update": "Store updated",
    "auditAction.backup_run": "Backup run",
    "auditAction.sale_created": "Sale completed",
    "auditAction.quotation_created": "Quotation created",
    "auditAction.quotation_deleted": "Quotation deleted",
    "auditAction.quotation_duplicated": "Quotation duplicated",
    "auditAction.quotation_printed": "Quotation printed",
    // Friendly labels for the entity tables shown in the Audit filter dropdown.
    "auditTable.customers": "Customers",
    "auditTable.drawer_sessions": "Drawer sessions",
    "auditTable.held_orders": "Held orders",
    "auditTable.quotations": "Quotations",
    "auditTable.sales": "Sales",
    "auditTable.users": "Users",
    "auditTable.products": "Products",
    "auditTable.suppliers": "Suppliers",
    "auditTable.stores": "Stores",
    "auditTable.expenses": "Expenses",
    "auditTable.transfers": "Transfers",
    "auditTable.promotions": "Promotions",
    "auditTable.settings": "Settings",
    "auditTable.stocktakes": "Stock counts",
    "auditTable.stock": "Stock",
    "auditTable.receivings": "Receivings",
    "auditTable.backups": "Backups",
    "auditTable.permissions": "Permissions",
    "auditTable.shifts": "Shifts",
    // ---------- Global Settings ----------
    "globalSettings.title": "Global Settings",
    "globalSettings.business": "Business",
    "globalSettings.tax": "Tax",
    "globalSettings.receipt": "Receipt",
    "globalSettings.loyalty": "Loyalty",
    "globalSettings.security": "Security",
    "globalSettings.backup": "Backup",
    "globalSettings.backupSuccess": "Backup completed.",
    "globalSettings.backupFailed": "Backup failed.",
    "globalSettings.save": "Save changes",
    "globalSettings.subtitle": "Organisation-wide configuration. Changes apply to all stores.",
    "globalSettings.notifications": "Notifications",
    "globalSettings.event": "Event",
    "globalSettings.email": "Email",
    "globalSettings.push": "Push",
    "globalSettings.lastBackup": "Last backup:",
    "globalSettings.never": "never",
    "globalSettings.schedule": "Schedule",
    "globalSettings.scheduleDaily": "Daily",
    "globalSettings.scheduleWeekly": "Weekly",
    "globalSettings.scheduleOff": "Off",
    "globalSettings.retentionDays": "Retention (days)",
    "globalSettings.running": "Running…",
    "globalSettings.backupNow": "Back up now",
    "globalSettings.recentBackups": "RECENT BACKUPS",
    "globalSettings.restoreBackup": "Restore this backup",
    "globalSettings.defaults": "Defaults",
    "globalSettings.defaultCurrency": "Default currency",
    "globalSettings.defaultTaxRate": "Default tax rate (%)",
    "globalSettings.locale": "Locale",
    "globalSettings.localeLao": "Lao",
    "globalSettings.localeEnglish": "English",
    "globalSettings.timezone": "Timezone",
    "globalSettings.taxMode": "Tax mode",
    "globalSettings.taxInclusive": "Inclusive",
    "globalSettings.taxExclusive": "Exclusive",
    "globalSettings.lowStockThreshold": "Low-stock threshold",
    "globalSettings.varianceThreshold": "Drawer difference threshold (₭)",
    "globalSettings.varianceHintPrefix": "Shifts that close with difference beyond",
    "globalSettings.varianceHintSuffix": "trigger an alert.",
    "globalSettings.license": "License",
    "globalSettings.plan": "PLAN",
    "globalSettings.seatsUsed": "SEATS USED",
    "globalSettings.seats": "SEATS",
    "globalSettings.expires": "EXPIRES",
    "globalSettings.manageLicense": "Manage license",
    "globalSettings.activating": "Activating…",
    "globalSettings.activate": "Activate",
    "globalSettings.licenseKey": "License key",
    "globalSettings.licenseActivated": "License activated.",
    "globalSettings.backupKindManual": "manual",
    "globalSettings.backupKindAuto": "auto",
    "globalSettings.backupStatusOk": "ok",
    "globalSettings.backupStatusRunning": "running",
    "globalSettings.backupStatusFailed": "failed",
    "globalSettings.licenseTierTrial": "trial",
    "globalSettings.licenseTierBasic": "basic",
    "globalSettings.licenseTierPro": "pro",
    "globalSettings.licenseStateValid": "Licensed",
    "globalSettings.licenseStateTrial": "Trial",
    "globalSettings.licenseStateInvalid": "Invalid",
    "globalSettings.licensedTo": "LICENSED TO",
    "globalSettings.unlicensed": "Unlicensed (trial)",
    "globalSettings.tillsUsed": "TILLS",
    "globalSettings.supportEnds": "SUPPORT ENDS",
    // ---------- Reports tab labels ----------
    "reports.tab.salesSummary": "Sales summary",
    "reports.tab.topProducts": "Top products",
    "reports.tab.deadStock": "Dead stock",
    "reports.tab.cashierPerformance": "Cashier performance",
    "reports.tab.profitMargin": "Profit margin",
    "reports.tab.paymentMethods": "Payment methods",
    "reports.tab.storeCompare": "Store comparison",
    "reports.tab.stockCard": "Stock card",
    "reports.tab.vat": "VAT",
    "reports.tab.arApAging": "Money owed and supplier bills",
    "reports.loading": "Loading report…",
    "reports.exporting": "Exporting…",
    "reports.lastNDays": "Last {n}d",
    // ---------- Stores cards / form ----------
    "stores.subtitleStores": "stores",
    "stores.subtitleWarehouse": "warehouse",
    "stores.subtitleActiveTills": "active tills",
    "stores.statusActive": "Active",
    "stores.statusOff": "Off",
    "stores.colCurrency": "CURRENCY",
    "stores.colTaxRate": "TAX RATE",
    "stores.colTimezone": "TIMEZONE",
    "stores.taxIncl": "incl.",
    "stores.taxExcl": "excl.",
    "stores.dailyPrefix": "Daily",
    "stores.weekdayPrefix": "Mon-Sat",
    "stores.closedSuffix": "closed",
    "stores.daysPerWeek": "days / week",
    "stores.closedAllWeek": "Closed all week",
    "stores.storeStaffLabel": "Store staff",
    "stores.storeStaffUnavailable": "Store staff (not yet available)",
    "stores.storeReportLabel": "Store report",
    "stores.storeReportUnavailable": "Store report (not yet available)",
    "stores.newStore": "New store",
    "stores.editStorePrefix": "Edit store —",
    "stores.fieldName": "Name",
    "stores.fieldTimezone": "Timezone",
    "stores.fieldCurrency": "Currency",
    "stores.fieldTaxRate": "Tax rate %",
    "stores.fieldTaxMode": "Tax mode",
    "stores.fieldLanguage": "Default language",
    "stores.fieldAddress": "Address",
    "stores.fieldReceiptHeader": "Receipt header",
    "stores.fieldReceiptFooter": "Receipt footer",
    "stores.fieldOpeningHours": "Opening hours",
    "stores.taxInclusiveOption": "Inclusive",
    "stores.taxExclusiveOption": "Exclusive",
    "stores.langLao": "Lao",
    "stores.langEnglish": "English",
    "stores.dayClosedLabel": "Closed",
    "stores.openDayLabel": "Open",
    "stores.closeDayLabel": "Close",
    "stores.openDayTitle": "Open this day",
    "stores.closeDayTitle": "Close this day",
    "stores.saving": "Saving…",
    "stores.dayMon": "Mon",
    "stores.dayTue": "Tue",
    "stores.dayWed": "Wed",
    "stores.dayThu": "Thu",
    "stores.dayFri": "Fri",
    "stores.daySat": "Sat",
    "stores.daySun": "Sun",
    // ---------- Staff (users) cards / table ----------
    "staff.subtitleUsers": "users",
    "staff.subtitleActive": "active",
    "staff.allRoles": "All roles",
    "staff.roleOwner": "Owner",
    "staff.roleManager": "Manager",
    "staff.roleCashier": "Cashier",
    "staff.ownerGlobalHint": "The owner has full access to every store and all settings — no store assignment needed.",
    "staff.colLastLogin": "Last login",
    "staff.colActive": "Active",
    "staff.statusOn": "On",
    "staff.statusOff": "Off",
    "staff.resetPasswordLabel": "Reset password",
    "staff.editUserLabel": "Edit user",
    "staff.saving": "Saving…",
    "staff.allStoresLabel": "All stores",
    "staff.editTitlePrefix": "Edit —",
    "staff.passwordTitlePrefix": "Reset password —",
    "staff.passwordTitleDefault": "Reset password",
    "staff.setPassword": "Set password",
    "staff.passwordMinError": "Password must be at least 8 characters",
    "staff.passwordTypeNewPrefix": "Type the new password for",
    "staff.passwordHandOff": "Hand it off in person — we don't store it in plaintext.",
    "staff.passwordPlaceholder": "Min. 8 characters",
    "staff.thisUserFallback": "this user",
    "staff.managerStoreRequiredHint": "Managers must be assigned to a specific store.",
    "staff.saveFailedPrefix": "Save failed",
    // ---------- Stock Count ----------
    "stockCount.noSession": "No active session",
    "stockCount.startNewHeading": "Start a new stock count",
    "stockCount.startNewDescription": "Loads every active product at the selected store with its expected qty. Scan each item to record what you actually have.",
    "stockCount.sessionPrefix": "Session",
    "stockCount.sessionStartedPrefix": "started",
    "stockCount.sessionByPrefix": "by",
    "stockCount.statusDraft": "Draft",
    "stockCount.statusInProgress": "In progress",
    "stockCount.statusSubmitted": "Submitted — awaiting approval",
    "stockCount.statusApproved": "Approved",
    "stockCount.statusCancelled": "Cancelled",
    "stockCount.pause": "Pause",
    "stockCount.submitting": "Submitting…",
    "stockCount.closeAndPost": "Close count & post",
    "stockCount.approve": "Approve",
    "stockCount.approving": "Approving…",
    "stockCount.kpiItemsCounted": "ITEMS COUNTED",
    "stockCount.kpiDifferences": "DIFFERENCES",
    "stockCount.kpiValueDiff": "VALUE DIFFERENCE",
    "stockCount.kpiDuration": "DURATION",
    "stockCount.scanPlaceholder": "Scan barcode to count…",
    "stockCount.manualEntry": "Manual entry",
    "stockCount.manualEntryLabel": "Manual stock-count entry by SKU",
    "stockCount.colDifference": "Difference",
    "stockCount.colStatus": "Status",
    "stockCount.statusPending": "Pending",
    "stockCount.statusOk": "OK",
    "stockCount.statusDifference": "Difference",
    "stockCount.starting": "Starting…",
    "stockCount.confirmStartPrefix": "Start a new stock count for",
    "stockCount.confirmStartSuffix": "? You'll need to scan items to record actual quantities.",
    // ---------- Stock by Store ----------
    "stockByStore.subtitleProducts": "products",
    "stockByStore.lowStockOnly": "Low stock only",
    "stockByStore.startCount": "Start count",
    "stockByStore.colProductHdr": "Product",
    "stockByStore.colSkuHdr": "SKU",
    "stockByStore.colTotalHdr": "Total",
    "stockByStore.colValueHdr": "Value",
    "stockByStore.adjustTitlePrefix": "Adjust —",
    "stockByStore.adjustTitleDefault": "Adjust stock",
    "stockByStore.applyAdjustment": "Apply adjustment",
    "stockByStore.currentQty": "Current qty:",
    "stockByStore.delta": "Delta (±)",
    "stockByStore.deltaPlaceholder": "e.g. -3 for damage, +10 for found",
    "stockByStore.newQty": "New qty:",
    "stockByStore.reason": "Reason",
    "stockByStore.reasonDamage": "Damage",
    "stockByStore.reasonTheft": "Theft",
    "stockByStore.reasonCount": "Count correction",
    "stockByStore.reasonFound": "Found",
    "stockByStore.reasonInitial": "Initial stock",
    "stockByStore.reasonOther": "Other",
    "stockByStore.noteOptional": "Note (optional)",
    "stockByStore.notePlaceholder": "e.g. 'Dropped crate, 3 bottles broke'",
    "stockByStore.saving": "Saving…",
    // ---------- Transfers ----------
    "transfers.subtitleMovements": "Between-store stock movements",
    "transfers.subtitleOutgoing": "outgoing",
    "transfers.subtitleIncoming": "incoming",
    "transfers.statusDraft": "Draft",
    "transfers.statusSent": "Sent",
    "transfers.statusInTransit": "In transit",
    "transfers.statusReceived": "Received",
    "transfers.statusCancelled": "Cancelled",
    "transfers.outgoing": "Outgoing",
    "transfers.incoming": "Incoming",
    "transfers.fromPrefix": "from",
    "transfers.toPrefix": "to",
    "transfers.toArrow": "→",
    "transfers.noOutgoing": "No outgoing transfers.",
    "transfers.noIncoming": "No incoming transfers.",
    "transfers.colValue": "VALUE",
    "transfers.colSent": "SENT",
    "transfers.colReceived": "RECEIVED",
    "transfers.send": "Send",
    "transfers.cancelLabel": "Cancel",
    "transfers.receive": "Receive",
    "transfers.confirmReceipt": "Confirm receipt",
    "transfers.saving": "Saving…",
    "transfers.receiveTitlePrefix": "Receive —",
    "transfers.receiveTitleDefault": "Receive transfer",
    "transfers.from": "From",
    "transfers.to": "To",
    "transfers.sentLabel": "Sent",
    "transfers.colSku": "SKU",
    "transfers.colProduct": "Product",
    "transfers.colQtySent": "Sent",
    "transfers.colQtyReceived": "Received",
    "transfers.colDifference": "Difference",
    "transfers.diffNotePlaceholder": "Note (optional) — e.g. 2 boxes damaged in transit",
    "transfers.diffWarning": "Difference lines will be flagged for sale review.",
    "transfers.newProductsHeading": "Products",
    "transfers.addProduct": "Add product",
    "transfers.noProductsYet": "No products added yet.",
    "transfers.colUnitCost": "Unit cost",
    "transfers.colQty": "Qty",
    "transfers.colTotal": "Total",
    "transfers.colLineTotal": "Line total",
    "transfers.sourceStore": "SOURCE STORE",
    "transfers.destStore": "DESTINATION STORE",
    "transfers.selectStore": "Select...",
    "transfers.lines": "Lines",
    "transfers.totalQty": "Total qty",
    "transfers.value": "Value",
    "transfers.notes": "NOTES",
    "transfers.notesPlaceholder": "Internal note for this transfer",
    "transfers.saveDraft": "Save draft",
    "transfers.saveChanges": "Save changes",
    "transfers.editDraftPrefix": "Edit draft -",
    "transfers.removePrefix": "Remove",
    "transfers.formChooseStores": "Choose a source and destination store.",
    "transfers.formDistinctStores": "Source and destination must be different stores.",
    "transfers.formNeedLine": "Add at least one product with quantity greater than zero.",
    "transfers.formNoNegativeCost": "Unit cost cannot be negative.",
    "transfers.byPrefix": "by",
    "transfers.detailCreated": "CREATED",
    "transfers.detailNotes": "NOTES",
    "transfers.noLineItems": "No line items.",
    // ---------- Receive Goods ----------
    "receiveGoods.newGrPrefix": "New GR ·",
    "receiveGoods.draftSuffix": "· draft",
    "receiveGoods.saveDraft": "Save draft",
    "receiveGoods.savePrintLabels": "Save & print labels",
    "receiveGoods.receiveAndPost": "Receive & post",
    "receiveGoods.saving": "Saving…",
    "receiveGoods.posting": "Posting…",
    "receiveGoods.supplierSelect": "Select…",
    "receiveGoods.purchaseOrder": "PURCHASE ORDER",
    "receiveGoods.poPlaceholder": "PO-0284 (no PO)",
    "receiveGoods.receiveDate": "RECEIVE DATE",
    "receiveGoods.store": "STORE",
    "receiveGoods.scanPlaceholder": "Scan barcode to add row…",
    "receiveGoods.noLines": "No lines yet. Scan a barcode or press Manual row.",
    "receiveGoods.colNo": "#",
    "receiveGoods.colProduct": "Product",
    "receiveGoods.colUnitCost": "Unit cost",
    "receiveGoods.colLineTotal": "Line total",
    "receiveGoods.internalNote": "INTERNAL NOTE",
    "receiveGoods.notePlaceholder": "e.g. 2 cases crushed on delivery",
    "receiveGoods.lines": "Lines",
    "receiveGoods.totalQty": "Total qty",
    "receiveGoods.subtotal": "Subtotal",
    "receiveGoods.totalCost": "Total cost",
    "receiveGoods.recentPurchases": "Recent purchases",
    "receiveGoods.removeLinePrefix": "Remove line",
    "receiveGoods.statusReceived": "received",
    "receiveGoods.statusDraft": "draft",
    "receiveGoods.statusPosted": "posted",
    "receiveGoods.statusPartial": "partial",
    "receiveGoods.statusCancelled": "cancelled",
    // ---------- Stock Movements ----------
    "stockMovements.subtitleEvents": "events today",
    "stockMovements.allTypes": "All types",
    "stockMovements.allProducts": "All products",
    "stockMovements.typeSale": "Sale",
    "stockMovements.typeReturn": "Return",
    "stockMovements.typeReceive": "Receive",
    "stockMovements.typeAdjust": "Adjust",
    "stockMovements.typeTransferIn": "Transfer in",
    "stockMovements.typeTransferOut": "Transfer out",
    "stockMovements.typeWaste": "Waste",
    "stockMovements.typeCountAdjust": "Count adjust",
    "stockMovements.actionSold": "Sold",
    "stockMovements.actionReturned": "Returned",
    "stockMovements.actionReceived": "Received",
    "stockMovements.actionAdjusted": "Adjusted",
    "stockMovements.actionTransferIn": "Transferred in",
    "stockMovements.actionTransferOut": "Transferred out",
    "stockMovements.actionWasted": "Wasted",
    "stockMovements.actionCountAdjust": "Count corrected",
    "stockMovements.pillSale": "sale",
    "stockMovements.pillReturn": "return",
    "stockMovements.pillReceive": "receive",
    "stockMovements.pillAdjust": "adjust",
    "stockMovements.pillTransferIn": "transfer in",
    "stockMovements.pillTransferOut": "transfer out",
    "stockMovements.pillWaste": "waste",
    "stockMovements.pillCountAdjust": "count adjust",
    "stockMovements.dateRangeSep": "to",
    "stockMovements.loadMore": "Load more",
    // ---------- Dashboard (manage/Dashboard.tsx) ----------
    "dashboard.perStoreShift": "Per-store shift status",
    "dashboard.shiftOpen": "Open",
    "dashboard.shiftClosed": "Closed",
    "dashboard.sinceLabel": "since",
    "dashboard.openSaleReviews": "Open sale reviews",
    "dashboard.review": "Review",
    "dashboard.top5Products": "Top 5 products today",
    "dashboard.top5Cashiers": "Top 5 cashiers today",
    "dashboard.lowStockAlerts": "Low stock alerts",
    "dashboard.revenuePrefix": "Revenue —",
    "dashboard.revenueTotal": "Total:",
    "dashboard.hourLabel": "hour",
    "dashboard.dayLabel": "day",
    "dashboard.withSalesSuffix": "with sales",
    "dashboard.period24h": "24h",
    "dashboard.period7d": "7d",
    "dashboard.period30d": "30d",
    "dashboard.period90d": "90d",
    "dashboard.periodCustom": "Custom",
    "dashboard.subtitleToday": "Today (hourly)",
    "dashboard.subtitle7d": "last 7 days",
    "dashboard.subtitle30d": "last 30 days",
    "dashboard.subtitle90d": "last 90 days",
    "dashboard.subtitleCustom": "custom range",
    "dashboard.hourly": "hourly",
    "dashboard.from": "From",
    "dashboard.to": "To",
    "dashboard.bucket": "Bucket",
    "dashboard.bucketHour": "Hour",
    "dashboard.bucketDay": "Day",
    "dashboard.hourlyHint": "Hourly bucket only available for ranges ≤ 14 days.",
    "dashboard.tooltipRevenue": "Revenue:",
    "dashboard.kpiTransactions": "Transactions",
    // ---------- SalesHistory ----------
    "salesHistory.subtitleSale": "sale in range",
    "salesHistory.subtitleSales": "sales in range",
    "salesHistory.walkIn": "Walk-in",
    "salesHistory.allTenders": "All tenders",
    "salesHistory.tenderCash": "Cash",
    "salesHistory.tenderQr": "QR",
    "salesHistory.tenderCard": "Card",
    "salesHistory.tenderCredit": "Credit",
    "salesHistory.tenderSplit": "Split",
    "salesHistory.statusPaid": "Paid",
    "salesHistory.statusVoided": "Voided",
    "salesHistory.statusRefund": "Refund",
    "salesHistory.rangeAll": "All",
    "salesHistory.rangeToday": "Today",
    "salesHistory.range7d": "7 days",
    "salesHistory.range30d": "30 days",
    "salesHistory.rangeCustom": "Custom…",
    "salesHistory.colReceiptHdr": "Receipt",
    "salesHistory.colTimeHdr": "Time",
    "salesHistory.colTenderHdr": "Tender",
    "salesHistory.colReferenceHdr": "Reference",
    "salesHistory.colCustomerHdr": "Customer",
    "salesHistory.colCashierHdr": "Cashier",
    "salesHistory.colTotalHdr": "Total",
    "salesHistory.colStatusHdr": "Status",
    "salesHistory.saleDetail": "Sale detail",
    "salesHistory.closeDrawer": "Close drawer",
    "salesHistory.voidSale": "Void sale",
    "salesHistory.requestVoid": "Request void",
    "salesHistory.voidConfirmBody": "Stock goes back on the shelf and the sale is removed from revenue. This cannot be undone.",
    "salesHistory.voidReasonLabel": "Reason (required)",
    "salesHistory.voidReasonPlaceholder": "Why is this sale being voided?",
    // ---------- Conflicts ----------
    "conflicts.subtitleWaiting": "waiting",
    "conflicts.subtitleHint": "sales the server pushed back after sync",
    "conflicts.acceptAllSimilar": "Accept all similar",
    "conflicts.kpiOpen": "Open",
    "conflicts.kpiResolved7d": "Resolved (7d)",
    "conflicts.kpiValueAtRisk": "Value at risk",
    "conflicts.kpiAvgResolution": "Avg resolution",
    "conflicts.filterAll": "All",
    "conflicts.filterOpen": "Open",
    "conflicts.filterAccepted": "Accepted",
    "conflicts.filterBackordered": "Backordered",
    "conflicts.filterCancelled": "Cancelled",
    "conflicts.statusNeedsReview": "Needs review",
    "conflicts.statusAccepted": "Accepted",
    "conflicts.statusCancelled": "Cancelled",
    "conflicts.statusBackordered": "Backordered",
    "conflicts.acceptAsIs": "Accept as-is",
    "conflicts.backorder": "Backorder",
    "conflicts.cancelRefund": "Cancel / refund",
    "conflicts.nothingToReview": "Nothing to review. All sales are settled.",
    "conflicts.resolvedHistory": "Resolved history",
    "conflicts.colId": "ID",
    "conflicts.colProduct": "Product",
    "conflicts.colResolution": "Resolution",
    "conflicts.colResolved": "Resolved",
    "conflicts.colAmount": "Amount",
    "conflicts.colNote": "Note",
    // ---------- Shifts & Drawers ----------
    "shiftsDrawers.subtitleShifts": "shifts window",
    "shiftsDrawers.subtitleOpen": "open",
    "shiftsDrawers.subtitleClosed": "closed",
    "shiftsDrawers.subtitleTotalDiff": "total difference",
    "shiftsDrawers.last7Days": "Last 7 days",
    "shiftsDrawers.rangeToday": "Today",
    "shiftsDrawers.rangeLast30Days": "Last 30 days",
    "shiftsDrawers.rangeAllTime": "All time",
    "shiftsDrawers.rangeCustom": "Custom range…",
    "shiftsDrawers.colShift": "Shift",
    "shiftsDrawers.colOpenedBy": "Opened by",
    "shiftsDrawers.colClosedBy": "Closed by",
    "shiftsDrawers.colOpened": "Opened",
    "shiftsDrawers.colOpening": "Opening",
    "shiftsDrawers.colExpectedHdr": "Expected",
    "shiftsDrawers.colCountedHdr": "Counted",
    "shiftsDrawers.colDifference": "Difference",
    "shiftsDrawers.colStatus": "Status",
    "shiftsDrawers.shiftOpen": "Open",
    "shiftsDrawers.shiftClosed": "Closed",
    "shiftsDrawers.drawerOpenLog": "Drawer open log",
    "shiftsDrawers.eventsCount": "events",
    "shiftsDrawers.drawerColTime": "Time",
    "shiftsDrawers.drawerColCashier": "Cashier",
    "shiftsDrawers.drawerColReason": "Reason",
    "shiftsDrawers.drawerColContext": "Context",
    "shiftsDrawers.reasonSale": "sale",
    "shiftsDrawers.reasonManual": "manual",
    "shiftsDrawers.reasonDrop": "drop",
    "shiftsDrawers.reasonCount": "count",
    "shiftsDrawers.antiTheft": "Anti-theft highlights",
    "shiftsDrawers.noFlags": "No flags this window.",
    "shiftsDrawers.severityHigh": "High",
    "shiftsDrawers.severityWatch": "Watch",
    "shiftsDrawers.diff7d": "Difference — 7d",
    "shiftsDrawers.net": "Net",
    // ---------- Expenses ----------
    "expenses.subtitleMonthly": "Monthly ledger",
    "expenses.subtitleEntries": "entries",
    "expenses.subtitleTotal": "total",
    "expenses.colIdHdr": "ID",
    "expenses.colDateHdr": "Date",
    "expenses.colCategoryHdr": "Category",
    "expenses.colDescriptionHdr": "Description",
    "expenses.colAmountHdr": "Amount",
    "expenses.colRecordedBy": "Recorded by",
    "expenses.colReceipt": "Receipt",
    "expenses.downloadReceipt": "Download receipt for",
    "expenses.kpiOther": "OTHER",
    "expenses.entry": "entry",
    "expenses.entries": "entries",
    "expenses.totalLabel": "Total",
    "expenses.dateRangeSep": "to",
    // ---------- Sync Queue ----------
    "syncQueue.title": "Sync Queue",
    "syncQueue.subtitleTotal": "total",
    "syncQueue.subtitleError": "error",
    "syncQueue.subtitlePending": "pending",
    "syncQueue.refresh": "Refresh",
    "syncQueue.clearSynced": "Clear synced",
    "syncQueue.kpiPending": "Pending",
    "syncQueue.kpiError": "Error",
    "syncQueue.kpiNeedsReview": "Needs review",
    "syncQueue.kpiSynced": "Synced",
    "syncQueue.errorsByMessage": "Errors grouped by message",
    "syncQueue.discardAll": "Discard all",
    "syncQueue.filterAll": "All",
    "syncQueue.filterError": "Error",
    "syncQueue.filterPending": "Pending",
    "syncQueue.filterNeedsReview": "Needs review",
    "syncQueue.filterSynced": "Synced",
    "syncQueue.allTypes": "All types",
    "syncQueue.typeSaleCreate": "Sale (create)",
    "syncQueue.typeSaleVoid": "Sale (void)",
    "syncQueue.typeStockAdjust": "Stock adjust",
    "syncQueue.typeDrawerOpen": "Drawer open",
    "syncQueue.typeDrawerClose": "Drawer close",
    "syncQueue.typeDrawerMovement": "Drawer movement",
    "syncQueue.typeExpenseCreate": "Expense",
    "syncQueue.typeHeldOrderSave": "Held order (save)",
    "syncQueue.typeHeldOrderCancel": "Held order (cancel)",
    "syncQueue.statusPending": "Pending",
    "syncQueue.statusSynced": "Synced",
    "syncQueue.statusNeedsReview": "Needs review",
    "syncQueue.statusError": "Error",
    "syncQueue.searchPlaceholder": "Search id, message, payload…",
    "syncQueue.failedToLoad": "Failed to load:",
    "syncQueue.eventsPrefix": "Events",
    "syncQueue.loading": "Loading…",
    "syncQueue.noEvents": "No events match these filters.",
    "syncQueue.colId": "ID",
    "syncQueue.colType": "Type",
    "syncQueue.colStatus": "Status",
    "syncQueue.colAttempts": "Attempts",
    "syncQueue.colCreated": "Created",
    "syncQueue.colLastAttempt": "Last attempt",
    "syncQueue.colMessage": "Server message / payload",
    "syncQueue.colActions": "Actions",
    "syncQueue.retry": "Retry",
    "syncQueue.discard": "Discard",
    "syncQueue.expandEventId": "Event ID",
    "syncQueue.expandServerRes": "Server resource",
    "syncQueue.expandServerMsg": "Server message",
    "syncQueue.expandPayload": "Payload",
    // ---------- Products ----------
    "products.subtitleActive": "active",
    "products.colProductHdr": "Product",
    "products.colIdHdr": "ID",
    "products.colBarcodeHdr": "Barcode",
    "products.colCategoryHdr": "Category",
    "products.colBrandHdr": "Brand",
    "products.colCostHdr": "Cost",
    "products.colSellHdr": "Sell",
    "products.colStockHdr": "Stock",
    "products.colStatusHdr": "Status",
    "products.statusActivePill": "Active",
    "products.statusInactivePill": "Inactive",
    "products.printLabels": "Print Labels",
    "products.filterAllStatuses": "All statuses",
    "products.filterActive": "Active",
    "products.filterInactive": "Inactive",
    "products.selectedSuffix": "selected",
    "products.bulkPriceAdjust": "Price adjust",
    "products.bulkActivate": "Activate",
    "products.bulkDeactivate": "Deactivate",
    "products.bulkPrintLabels": "Print labels",
    "products.selectAllPage": "Select all on page",
    "products.deselectAllPage": "Deselect all on page",
    "products.editAriaPrefix": "Edit",
    // ---------- Categories & Brands ----------
    "categoriesBrands.subtitle": "Tree-style category organisation · flat brand list",
    "categoriesBrands.newTopCategory": "New top-level category name…",
    "categoriesBrands.newBrand": "New brand name…",
    "categoriesBrands.brandHdr": "Brand",
    "categoriesBrands.productsHdr": "Products",
    "categoriesBrands.onHandValueHdr": "On-hand value",
    "categoriesBrands.brandActive": "Active",
    "categoriesBrands.brandInactive": "Inactive",
    "categoriesBrands.subBadge": "sub",
    "categoriesBrands.collapse": "Collapse",
    "categoriesBrands.expand": "Expand",
    "categoriesBrands.moveUp": "Move up",
    "categoriesBrands.moveDown": "Move down",
    "categoriesBrands.addSubCategory": "Add sub-category",
    "categoriesBrands.rename": "Rename",
    "categoriesBrands.deleteCategory": "Delete category",
    "categoriesBrands.renameBrand": "Rename brand",
    "categoriesBrands.deleteBrand": "Delete brand",
    "categoriesBrands.noCategories": "No categories yet — add one above.",
    "categoriesBrands.noBrands": "No brands yet.",
    "categoriesBrands.newSubCatTitle": "New sub-category",
    "categoriesBrands.cancel": "Cancel",
    "categoriesBrands.ok": "OK",
    "categoriesBrands.subCatName": "Sub-category name",
    "categoriesBrands.underPrefix": "· under",
    "categoriesBrands.subCatPlaceholder": "e.g. Cattle feed",
    "categoriesBrands.deleteCatTitle": "Delete category",
    "categoriesBrands.deleteBrandTitle": "Delete brand",
    "categoriesBrands.deleteConfirm": "Delete",
    "categoriesBrands.deleteCatBodyPrefix": "Delete category “",
    "categoriesBrands.deleteCatBodySuffix": "”? Products in it become uncategorised.",
    "categoriesBrands.deleteBrandBodyPrefix": "Delete brand “",
    "categoriesBrands.deleteBrandBodySuffix": "”? This will deactivate it.",
    // ---------- Promotions ----------
    "promotions.subtitleActive": "active",
    "promotions.subtitlePaused": "paused",
    "promotions.subtitleUsesMonth": "uses this month",
    "promotions.filterAll": "All",
    "promotions.filterActive": "Active",
    "promotions.filterPaused": "Paused",
    "promotions.filterScheduled": "Scheduled",
    "promotions.filterExpired": "Expired",
    "promotions.statusActive": "Active",
    "promotions.statusPaused": "Paused",
    "promotions.statusScheduled": "Scheduled",
    "promotions.statusExpired": "Expired",
    "promotions.typePercentOffCat": "Percent off category",
    "promotions.typeAmountOffProd": "Amount off product",
    "promotions.typeBuyXGetY": "Buy X get Y",
    "promotions.typeBundlePrice": "Bundle price",
    "promotions.typeSpendThreshold": "Spend threshold",
    "promotions.typeTimeWindow": "Time window",
    "promotions.colStartsHdr": "STARTS",
    "promotions.colEndsHdr": "ENDS",
    "promotions.usesSuffix": "uses this month",
    "promotions.liftSuffix": "lift",
    "promotions.editLabel": "Edit",
    "promotions.resumeLabel": "Resume",
    "promotions.pauseLabel": "Pause",
    "promotions.deleteLabel": "Delete",
    "promotions.previewOffSuffix": "off",
    "promotions.previewBuyPrefix": "Buy",
    "promotions.previewGetMiddle": "get",
    "promotions.previewFreeSuffix": "free",
    "promotions.previewBundlePrefix": "Bundle of",
    "promotions.previewItemsArrow": "items →",
    "promotions.previewSpendPrefix": "Spend",
    "promotions.previewSpendGetSuffix": "get",
    "promotions.previewPercentOff": "% off",
    // ---------- Customers ----------
    "customers.subtitleCustomers": "customers",
    "customers.subtitleOweMoney": "customers owe money",
    "customers.subtitleLoyaltyYtd": "loyalty pts YTD",
    "customers.sendStatements": "Send statements",
    "customers.sendingPrefix": "Sending",
    "customers.colNameHdr": "Name",
    "customers.colPhoneHdr": "Phone",
    "customers.colEmailHdr": "Email",
    "customers.colTierHdr": "Tier",
    "customers.colBalanceHdr": "Customer money owed",
    "customers.colCreditHdr": "Store credit",
    "customers.colPointsHdr": "Loyalty pts",
    "customers.colLastVisitHdr": "Last visit",
    "customers.allTiers": "All tiers",
    "customers.tierBronze": "Bronze",
    "customers.tierSilver": "Silver",
    "customers.tierGold": "Gold",
    "customers.tierPlatinum": "Platinum",
    "customers.tierDefault": "Default (Bronze)",
    "customers.owesMoney": "Owes money",
    "customers.drawerCustomerLabel": "Customer",
    "customers.closeDrawer": "Close",
    "customers.kpiTotalSpend": "TOTAL SPEND",
    "customers.kpiVisits": "VISITS",
    "customers.kpiAvgTicket": "AVG TICKET",
    "customers.kpiLoyaltyPts": "LOYALTY PTS",
    "customers.moneyOwedHeading": "Customer money owed",
    "customers.deltaPlaceholder": "Delta (±₭)",
    "customers.reasonPlaceholder": "Reason",
    "customers.adjustCredit": "Adjust credit",
    "customers.sendStatement": "Send statement",
    "customers.purchaseHistory": "Purchase history",
    "customers.salesCount": "sales",
    "customers.noPurchases": "No purchases yet.",
    "customers.histReceipt": "Receipt",
    "customers.histDate": "Date",
    "customers.histTotal": "Total",
    "customers.histTender": "Tender",
    "customers.fieldName": "Name",
    "customers.fieldPhone": "Phone",
    "customers.fieldEmail": "Email (optional)",
    "customers.fieldTier": "Tier (optional)",
    "customers.fieldNotes": "Notes (optional)",
    "customers.fullNamePlaceholder": "Full name",
    "customers.phonePlaceholder": "+856 20 …",
    "customers.emailPlaceholder": "name@example.la",
    "customers.notesPlaceholder": "Anything the cashier should know",
    "customers.saving": "Saving…",
    // ---------- Suppliers ----------
    "suppliers.subtitleSuppliers": "suppliers",
    "suppliers.subtitleBillsToPay": "supplier bills to pay",
    "suppliers.billsByAge": "Supplier bills by age",
    "suppliers.kpiBillsToPay": "SUPPLIER BILLS TO PAY",
    "suppliers.kpiSuppliersWeOwe": "SUPPLIERS WE OWE",
    "suppliers.kpiProductsSupplied": "PRODUCTS SUPPLIED",
    "suppliers.kpiDue30": "DUE > 30D",
    "suppliers.hasBillsToPay": "Has bills to pay",
    "suppliers.colSupplierHdr": "Supplier",
    "suppliers.colContactHdr": "Contact",
    "suppliers.colPhoneHdr": "Phone",
    "suppliers.colProductsHdr": "Products",
    "suppliers.colApHdr": "Supplier bills to pay",
    "suppliers.colLastHdr": "Last order",
    "suppliers.drawerSupplierLabel": "Supplier",
    "suppliers.closeDrawer": "Close",
    "suppliers.kpiAvgOrder": "AVG ORDER",
    "suppliers.kpiLastOrder": "LAST ORDER",
    "suppliers.billsByAgeSection": "Supplier bills by age",
    "suppliers.productsSupplied": "Products supplied",
    "suppliers.histProduct": "Product",
    "suppliers.histSku": "SKU",
    "suppliers.histUnitCost": "Unit cost",
    "suppliers.noProductsLinked": "No products linked to this supplier.",
    "suppliers.priceHistoryPrefix": "Price history —",
    "suppliers.closeChart": "Close chart",
    "suppliers.paySupplier": "Pay supplier",
    "suppliers.amountPlaceholder": "Amount (₭)",
    "suppliers.recordPayment": "Record payment",
    "suppliers.amountExceedsPrefix": "Amount exceeds supplier bills to pay",
};
export default en;
//# sourceMappingURL=en.js.map